export type Province = {
  id: string;
  name: string;
};

export type ProvinceQueue = {
  id: string;
  name: string;
};

export type ProvinceAssignments = Record<string, string | null>;

export type ProvinceQueueConfiguration = {
  queues: ProvinceQueue[];
  assignments: ProvinceAssignments;
};

const DEFAULT_QUEUE_CONTENT = [
  {
    name: 'Abruzzo - Molise',
    provinces: ["Chieti", "L'Aquila", 'Pescar', 'Teramo', 'Campobasso', 'Isernia'],
  },
  {
    name: 'Campania - Basilicata',
    provinces: ['Avellino', 'Benevento', 'Caserta', 'Napoli', 'Salerno', 'Matera', 'Potenza'],
  },
  {
    name: 'Calabria',
    provinces: ['Catanzano', 'Cosenza', 'Reggio Calabria', 'Crotone', 'Vibo Valentia'],
  },
  {
    name: 'Emilia Romagna',
    provinces: ['Bologna', 'Ferrara', 'Forlì', 'Cesena', 'Modena', 'Parma', 'Piacenza', 'Ravenna', 'Reggio Emilia', 'Rimini'],
  },
  {
    name: 'Trentino Alto Adige - Friuli Venezia Giulia',
    provinces: ['Trento', 'Bolzano', 'Gorizia', 'Pordenone', 'Trieste', 'Udine'],
  },
  {
    name: 'Lazio',
    provinces: ['Frosinone', 'Latina', 'Rieti', 'Viterbo', 'Città in provincia di Roma'],
  },
  {
    name: 'Piemonte Sud - Liguria',
    provinces: ['Alessandria', 'Asti', 'Cuneo', 'Genova', 'Imperia', 'La Spezia', 'Savona'],
  },
  {
    name: 'Lombardia Est',
    provinces: ['Bergamo', 'Brescia', 'Cremona', 'Mantova', 'Sondrio'],
  },
  {
    name: 'Lombardia Ovest',
    provinces: ['Como', 'Lecco', 'Lodi', 'Milano', 'Monza-Brianza', 'Pavia', 'Varese'],
  },
  {
    name: 'Umbria - Marche',
    provinces: ['Perugia', 'Terni', 'Ancona', 'Ascoli Piceno', 'Fermo', 'Macerata', 'Pesaro-Urbino'],
  },
  {
    name: "Piemonte Nord - Valle d'Aosta",
    provinces: ['Biella', 'Novara', 'Torino', 'Verbano-Cusio-Ossola', 'Vercelli'],
  },
  {
    name: 'Puglia',
    provinces: ['Bari', 'Barletta-Andria-Trani', 'Brindisi', 'Foggia', 'Lecce', 'Taranto'],
  },
  {
    name: 'Sardegna',
    provinces: ['Cagliari', 'Sassari', 'Oristano', 'Nuoro', 'Sud Sardegna'],
  },
  {
    name: 'Sicilia',
    provinces: ['Agrigento', 'Caltanissetta', 'Catania', 'Enna', 'Messina', 'Palermo', 'Ragusa', 'Siracusa', 'Trapani'],
  },
  {
    name: 'Toscana',
    provinces: ['Arezzo', 'Firenze', 'Grosseto', 'Livorno', 'Lucca', 'Massa-Carrara', 'Pisa', 'Pistoia', 'Prato', 'Siena'],
  },
  {
    name: 'Veneto',
    provinces: ['Belluno', 'Padova', 'Rovigo', 'Treviso', 'Venezia', 'Verona', 'Vicenza'],
  },
  {
    name: 'Roma - Lazio',
    provinces: ['Roma (solo Roma comune, non inseriamo le province)'],
  },
] as const;

function slug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export const DEFAULT_PROVINCE_QUEUES: ProvinceQueue[] = DEFAULT_QUEUE_CONTENT.map(({ name }) => ({
  id: slug(name),
  name,
}));

export const QUEUE_PROVINCES: Province[] = DEFAULT_QUEUE_CONTENT.flatMap(({ provinces }) => (
  provinces.map((name) => ({ id: slug(name), name }))
));

export const DEFAULT_PROVINCE_ASSIGNMENTS: ProvinceAssignments = Object.fromEntries(
  DEFAULT_QUEUE_CONTENT.flatMap(({ name, provinces }) => {
    const queueId = slug(name);
    return provinces.map((province) => [slug(province), queueId]);
  }),
);

function isStoredQueue(value: unknown): value is ProvinceQueue {
  if (!value || typeof value !== 'object') return false;
  const queue = value as Record<string, unknown>;
  return typeof queue.id === 'string'
    && /^[a-z0-9][a-z0-9-]{0,79}$/.test(queue.id)
    && typeof queue.name === 'string'
    && queue.name.trim().length > 0
    && queue.name.trim().length <= 100;
}

export function defaultProvinceQueueConfiguration(): ProvinceQueueConfiguration {
  return {
    queues: DEFAULT_PROVINCE_QUEUES.map((queue) => ({ ...queue })),
    assignments: { ...DEFAULT_PROVINCE_ASSIGNMENTS },
  };
}

export function storedProvinceQueueConfiguration(value: unknown): ProvinceQueueConfiguration {
  if (!value || typeof value !== 'object') return defaultProvinceQueueConfiguration();

  const stored = value as Record<string, unknown>;
  const queueCandidates = Array.isArray(stored.queues)
    ? stored.queues.filter(isStoredQueue).map((queue) => ({ id: queue.id, name: queue.name.trim() }))
    : [];
  const uniqueQueues = queueCandidates.filter((queue, index) => (
    queueCandidates.findIndex((candidate) => candidate.id === queue.id) === index
  ));
  const queues = uniqueQueues.length > 0
    ? uniqueQueues
    : DEFAULT_PROVINCE_QUEUES.map((queue) => ({ ...queue }));
  const queueIds = new Set(queues.map(({ id }) => id));
  const storedAssignments = stored.assignments && typeof stored.assignments === 'object'
    ? stored.assignments as Record<string, unknown>
    : {};

  const assignments = Object.fromEntries(QUEUE_PROVINCES.map(({ id }) => {
    const savedQueue = storedAssignments[id];
    if (savedQueue === null || (typeof savedQueue === 'string' && queueIds.has(savedQueue))) {
      return [id, savedQueue];
    }

    const defaultQueue = DEFAULT_PROVINCE_ASSIGNMENTS[id];
    return [id, defaultQueue && queueIds.has(defaultQueue) ? defaultQueue : null];
  }));

  return { queues, assignments };
}

export function createProvinceQueueId(name: string, queues: readonly ProvinceQueue[]): string {
  const baseId = slug(name) || 'nuova-coda';
  const existingIds = new Set(queues.map(({ id }) => id));
  if (!existingIds.has(baseId)) return baseId;

  let suffix = 2;
  while (existingIds.has(`${baseId}-${suffix}`)) suffix += 1;
  return `${baseId}-${suffix}`;
}

export function queueNameExists(name: string, queues: readonly ProvinceQueue[]): boolean {
  const normalized = name.trim().toLocaleLowerCase('it-IT');
  return queues.some((queue) => queue.name.toLocaleLowerCase('it-IT') === normalized);
}

export function provincesInQueue(assignments: ProvinceAssignments, queueId: string): Province[] {
  return QUEUE_PROVINCES.filter(({ id }) => assignments[id] === queueId);
}

export function unassignedQueueProvinces(assignments: ProvinceAssignments): Province[] {
  return QUEUE_PROVINCES.filter(({ id }) => assignments[id] === null);
}
